/**
 * Event-related TypeScript interfaces and types for Mirsal Event Management System
 */

export interface EventCategory {
  id: number;
  name: string;
  name_ar?: string;
  events_count?: number; // Number of events in this category
}

export interface EventSeries {
  id: string;
  name: string;
  description?: string;
  category?: EventCategory;
  category_id?: number;
  category_name?: string;
  host?: User;
  host_id?: string;
  host_name?: string;
  overall_guest_limit?: number;
  allow_companions: boolean;
  status: 'planning' | 'active' | 'completed' | 'canceled';
  access_control_flags?: Record<string, any>;
  created_at: string;
  updated_at?: string;
  total_events?: number;
  series_date_range?: string;
  events?: Event[];
  events_count?: number; // Legacy compatibility
}

// Form data types for creating/updating
export interface CategoryFormData {
  name: string;
  name_ar: string;
}

// API Response wrapper type
export interface APIResponse<T> {
  data: T;
  success: boolean;
  message?: string;
}

// Django Rest Framework Pagination Response
export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

export interface User {
  id: string;
  email?: string;
  phone?: string;
  first_name: string;
  last_name: string;
  role: 'admin' | 'host' | 'guest' | 'usher';
  is_active: boolean;
  created_at: string;
  preferred_language: string;
  get_full_name?: string;
}

export type EventStatus = 'pending' | 'active' | 'rescheduled' | 'completed' | 'canceled' | 'archived' | 'rejected';

// Helper function to determine effective event status including pending approval
export const getEffectiveEventStatus = (event: Event): EventStatus => {
  // Debug logging removed for performance
  
  // Check if event was rejected (highest priority)
  if (event.access_control_flags?.approval_status === 'rejected') {
    return 'rejected';
  }
  
  // Check if event is pending approval
  if (event.access_control_flags?.approval_status === 'pending') {
    return 'pending';
  }
  
  // Check if event requires approval but hasn't been processed yet
  if (event.access_control_flags?.requires_approval === true && 
      !event.access_control_flags?.approval_status) {
    return 'pending';
  }
  
  // Return the actual backend status for approved/active events
  return event.status;
};

export type GuestEventStatus = 'pending' | 'accepted' | 'rejected' | 'ignored';

export interface Event {
  id: string;
  title: string;
  event_date: string; // ISO date string
  event_time: string; // HH:MM format
  location: string;
  description?: string;
  category?: EventCategory;
  category_name?: string; // Category name from list serializer
  series?: EventSeries;
  series_name?: string;
  series_position?: number;
  is_part_of_series?: boolean;
  is_main_event?: boolean;
  is_main_event_in_series?: boolean; // Computed property: true if this is the main event in its series
  host: User;
  host_name?: string; // Full name of the event host
  created_by?: User;
  guest_limit?: number;
  allow_companions: boolean;
  companion_limit?: number;
  editable_until?: string; // Duration string
  editing_window_hours?: number; // Hours before event when editing is allowed
  status: EventStatus;
  message_template?: string;
  link_expiry?: string; // ISO datetime string
  access_control_flags: Record<string, any>;
  rejection_reason?: string; // Reason for rejection when status is rejected (computed from access_control_flags)
  created_at: string; // ISO datetime string
  
  // Phase 2 New Fields
  invitation_link_uuid: string;
  invitation_url: string;
  is_editable: boolean;
  can_edit_event?: boolean; // Whether the event can be edited by hosts/admins
  editing_window_ends_at?: string; // ISO datetime when editing window ends
  time_until_event: string;
  
  // Computed properties
  is_active?: boolean;
  is_full?: boolean;
  can_edit_responses?: boolean;
  guest_count?: number;
  accepted_count?: number;
  pending_count?: number;
  rejected_count?: number;
  
  // Image fields
  image_url?: string;
  image_id?: string;
  image_shape?: 'circle' | 'square' | 'heart' | 'star';
  background_theme?: string;
  
  // Optional event links and information
  location_link?: string;
  website_link?: string;
  dress_code?: string;
  
  // Invitation display settings
  show_total_guests_invited?: boolean;
  
  // Secure slug for URL routing (generated from event ID)
  slug?: string;
  
  // Usher service fields
  usher_service_enabled?: boolean;
  usher_service_fee_charged?: boolean;
  can_enable_usher_service?: boolean;
}

export interface EventHistoryEntry {
  id: number;
  change_type: string;
  field_name?: string;
  old_value?: string;
  new_value?: string;
  changed_by?: User;
  changed_at: string;
  metadata?: Record<string, any>;
}

export interface GuestEvent {
  id: number;
  guest: User;
  event: Event;
  created_by?: User;
  created_at?: string; // ISO datetime string;
  status: GuestEventStatus;
  companion_name?: string;
  companion_count?: number;
  invite_sent_at?: string;
  response_at?: string;
}

// Enhanced Series Types for Multiple Events

export interface EventInSeriesFormData {
  id: string; // temporary ID for form management
  title: string;
  event_date: string;
  event_time: string;
  location: string;
  description?: string;
  category_id?: number;
  guest_limit?: number;
  editable_until?: string;
  editing_window_hours?: number;
  message_template?: string;
  location_link?: string;
  website_link?: string;
  dress_code?: string;
  usher_service_enabled?: boolean;
}

export interface SeriesFormData {
  name: string;
  description?: string;
  category_id?: number;
  host_id?: string; // For admin to assign specific host
  overall_guest_limit?: number;
  events: EventInSeriesFormData[];
  access_control_flags?: Record<string, any>;
  usher_service_enabled?: boolean;
}

// Legacy type for backward compatibility
export interface SeriesFormItem {
  id: string; // temporary ID for form management
  name: string;
  description?: string;
}

export interface EventFormData {
  title: string;
  event_date: string;
  event_time: string;
  location: string;
  description?: string;
  category_id?: number;
  series_id?: string; // UUID of existing series to add event to
  series_list?: SeriesFormItem[]; // New: array of series
  host_id?: string; // New: for admin to assign host
  guest_limit?: number;
  editable_until?: string;
  editing_window_hours?: number;
  message_template?: string;
  link_expiry?: string;
  access_control_flags?: Record<string, any>;
  
  // New unified series creation fields
  is_series?: boolean;
  series_name?: string;
  series_description?: string;
  series_overall_guest_limit?: number;
  additional_events?: EventInSeriesFormData[]; // Additional events if creating a series
  
  // Image fields
  image_url?: string;
  image_id?: string;
  image_shape?: 'circle' | 'square' | 'heart' | 'star';
  background_theme?: string;
  
  // Optional event links and information
  location_link?: string;
  website_link?: string;
  dress_code?: string;
  
  // Invitation display settings
  show_total_guests_invited?: boolean;
  
  // Usher service
  usher_service_enabled?: boolean;
}

export interface EventFilters {
  search?: string;
  status?: EventStatus[];
  category_id?: number;
  series_id?: string;
  host_id?: string;
  created_by?: string;
  date_from?: string;
  date_to?: string;
  location?: string;
  page?: number;
  page_size?: number;
  ordering?: string;
  series_main_only?: boolean; // Filter to show only main events from series (default: true)
}

export interface EventListResponse {
  events: Event[];
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
}

export interface EventStats {
  total_events: number;
  active_events: number;
  upcoming_events: number;
  past_events: number;
  total_guests: number;
  accepted_guests: number;
  pending_guests: number;
}

// Phase 2 New Interfaces
export interface EventPermissions {
  can_view: boolean;
  can_edit: boolean;
  can_delete: boolean;
  can_cancel: boolean;
  can_reschedule: boolean;
  can_archive: boolean;
  can_duplicate: boolean;
  can_reactivate: boolean;
  can_manage_guests: boolean;
  can_invite_guests: boolean;
  can_see_guest_list: boolean;
  can_export_data: boolean;
  can_change_settings: boolean;
  allowed_actions: string[];
  allowed_status_transitions: string[];
  restrictions: string[];
  role_context: {
    is_owner: boolean;
    is_admin: boolean;
    is_host: boolean;
    is_guest: boolean;
  };
}

export interface StatusTransitionOptions {
  current_status: EventStatus;
  available_transitions: EventStatus[];
  transition_reasons: Record<EventStatus, string[]>;
}

export interface EventActionData {
  reason?: string;
  notify_guests?: boolean;
  new_date?: string;
  new_time?: string;
  new_location?: string;
  notes?: string;
}

export interface InvitationLinkData {
  uuid: string;
  invitation_url: string;
  share_text?: string;
  email_subject?: string;
  event_title?: string;
  qr_code_url?: string;
  expires_at?: string;
}

export interface EventValidationError {
  field: string;
  message: string;
  code: string;
}

export interface BusinessRuleValidation {
  is_valid: boolean;
  errors: EventValidationError[];
  warnings: EventValidationError[];
}
